import React from 'react';
import { motion } from 'framer-motion';
import { Users, Clock, Zap, Eye, BarChart3 } from 'lucide-react';
import CategoryIcon from './CategoryIcon';
import MechanicalOdometer from './MechanicalOdometer';

/**
 * PollHero (V2.2 - Industrial HUD Cleanup)
 * Manages the background image and optional HUD overlay for standard cards.
 * If 'compact' is true, it remains as a pure background, letting the PollCard overlay handle metrics.
 *
 * `img` is overloaded: it's either a single image URL/string (most polls), or an array of
 * image URLs when the poll has per-option images (see `optionImages` in PollCard.js, passed
 * in as `img` when `id === 'preview'` or when a poll was created with multiple option
 * images) — `hasMultiple` below is what distinguishes the two, and `layout` ('columns' |
 * 'rows' | 'grid') picks how the collage of multiple images is skewed/tiled. On a broken/
 * missing single image, `onError` swaps in `fallbackImg` exactly once (guarded by the
 * `imgSrc !== fallbackImg` check so a broken fallback can't retrigger itself in a loop).
 */
const PollHero = ({ img, category, watchingCount, pulsedGhosts, timeLeft, formatTimeLeft, inModal, compact, isTrending, layout = 'auto' }) => {
    // DEFAULT DUMMY IMAGE (Sleek Dark Branding)
    const fallbackImg = 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=1200'; // Sleek abstract dark gradient
    const [imgSrc, setImgSrc] = React.useState(img || fallbackImg);
    const containerClasses = `w-full h-[160px] relative overflow-hidden select-none bg-gray-900`;

    // Multi-image logic
    // We assume 'img' can be an array of strings, or we might get 'optionImages' prop
    const images = Array.isArray(img) ? img : [imgSrc];
    const hasMultiple = images.length > 1;

    // Sync state if prop changes
    React.useEffect(() => {
        if (img) setImgSrc(img);
        else setImgSrc(fallbackImg);
    }, [img, fallbackImg]);

    return (
        <div className={containerClasses}>
            {/* 1. Background Image(s) + Base Glow */}
            <div className={`absolute -inset-10 opacity-[0.2] blur-[100px] transition-colors duration-1000 ${category === 'Politics' ? 'bg-red-500' : 'bg-primary-600'}`} />
            
            {hasMultiple ? (
                <div className="w-full h-full relative overflow-hidden select-none">
                    {layout === 'columns' && (
                        <div className="flex h-full w-full -skew-x-6 scale-110 origin-center">
                            {images.map((src, i) => (
                                <div key={i} className="flex-1 h-full overflow-hidden border-r border-white/20 relative">
                                    <motion.img
                                        initial={{ scale: 1.3, opacity: 0 }}
                                        animate={{ scale: 1.25, opacity: 1 }}
                                        transition={{ duration: 0.8, delay: i * 0.1 }}
                                        src={src || fallbackImg}
                                        className="w-full h-full object-cover skew-x-6"
                                        alt=""
                                    />
                                    <div className="absolute inset-0 bg-black/10" />
                                </div>
                            ))}
                        </div>
                    )}

                    {layout === 'rows' && (
                        <div className="flex flex-col h-full w-full -skew-y-3 scale-110 origin-center">
                            {images.map((src, i) => (
                                <div key={i} className="flex-1 w-full overflow-hidden border-b border-white/20 relative">
                                    <motion.img
                                        initial={{ scale: 1.3, opacity: 0 }}
                                        animate={{ scale: 1.25, opacity: 1 }}
                                        transition={{ duration: 0.8, delay: i * 0.1 }}
                                        src={src || fallbackImg}
                                        className="w-full h-full object-cover skew-y-3 animate-none"
                                        alt=""
                                    />
                                    <div className="absolute inset-0 bg-black/10" />
                                </div>
                            ))}
                        </div>
                    )}

                    {(layout === 'grid' || (layout !== 'columns' && layout !== 'rows')) && (
                        <div className={`grid h-full w-full -skew-x-6 scale-115 origin-center ${
                            images.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'
                        }`}>
                            {images.slice(0, 4).map((src, i) => (
                                <div key={i} className="relative overflow-hidden border border-white/10">
                                    <motion.img
                                        initial={{ scale: 1.35, opacity: 0 }}
                                        animate={{ scale: 1.3, opacity: 1 }}
                                        transition={{ duration: 0.8, delay: i * 0.1 }}
                                        src={src || fallbackImg}
                                        className="w-full h-full object-cover skew-x-6"
                                        alt=""
                                    />
                                    <div className="absolute inset-0 bg-black/10" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <img
                    src={imgSrc}
                    onError={() => {
                        if (imgSrc !== fallbackImg) setImgSrc(fallbackImg);
                    }}
                    className="w-full h-full object-cover scale-105 group-hover/card:scale-110 transition-transform duration-[6s] ease-out select-none pointer-events-none"
                    alt="background"
                />
            )}

            {/* 2. Cinematic Shadows & Gradients (Light/Dark Theme Aware) */}
            <div className="absolute inset-0 bg-black/10 dark:bg-black/65 transition-colors duration-300 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 dark:from-black/75 via-transparent to-transparent transition-all duration-300 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 dark:from-black/45 via-transparent to-transparent transition-all duration-300 pointer-events-none" />

            {/* 3. UNIVERSAL OVERLAY HUD (Always visible now) */}
            <div className="absolute inset-0 z-10 p-5 flex flex-col pointer-events-none">
                {/* Top Row: Trending (Minimal) */}
                <div className="flex items-start justify-end w-full">
                    {isTrending && (
                        <motion.div
                            initial={{ scale: 0 }} animate={{ scale: 1 }}
                            className="bg-orange-500 text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg border border-orange-400/50"
                        >
                            <Zap size={14} className="fill-white" />
                            <span className="text-2xs font-black uppercase tracking-tighter">Trending</span>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PollHero;
